import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Grid,
  Box,
  Chip,
} from '@mui/material';
import {
  People,
  Business,
  Folder,
  Send,
  WbSunny,
  Brightness3,
  WbTwilight,
  InsertDriveFile,
} from '@mui/icons-material';
import { useAuth } from '../../../hooks/useAuth';
import { usePermissions } from '../../../hooks/usePermissions';
import StatCard from '../components/StatCard';
import QuickAccessWidget from '../components/QuickAccessWidget';
import LoadingLogo from '../../../components/LoadingLogo';
import PageHeader from '../../../components/PageHeader';
import { dashboardService } from '../services/dashboardService';

const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour >= 6 && hour < 12) return { text: 'Buenos dias', icon: WbSunny };
  if (hour >= 12 && hour < 19) return { text: 'Buenas tardes', icon: WbTwilight };
  return { text: 'Buenas noches', icon: Brightness3 };
};

const getFormattedDate = () => {
  return new Date().toLocaleDateString('es-MX', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const DashboardPage = () => {
  const { user, isOwner } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);

  // Check if user has permission
  const hasPermission = (permission) => {
    if (!permission) return true;
    if (!user?.allPermissions) return false;
    if (user.role === 'SUPER_ADMIN') return true;
    return user.allPermissions.includes(permission);
  };

  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setLoading(true);
        const statsData = await dashboardService.getStats();
        setStats(statsData);
      } catch (error) {
        console.error('Error loading dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, []);

  const quickActions = [
    {
      title: 'Usuarios',
      icon: <People />,
      color: '#5980a6',
      path: '/users',
      permission: 'user.view',
    },
    {
      title: 'Empresas',
      icon: <Business />,
      color: '#5980a6',
      path: isOwner ? '/companies' : `/companies/${user?.companyId}`,
      permission: 'company.view',
    },
    {
      title: 'Expedientes',
      icon: <Folder />,
      color: '#5980a6',
      path: '/proceedings',
      permission: 'proceeding.view',
    },
    {
      title: 'Correspondencia',
      icon: <Send />,
      color: '#5980a6',
      path: '/correspondences',
      permission: 'correspondence.view',
    },
  ].filter(action => hasPermission(action.permission));

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <LoadingLogo size={120} />
      </Box>
    );
  }

  const greeting = getGreeting();
  const firstName = user?.name?.split(' ')[0] || 'Usuario';

  return (
    <Box>
      <PageHeader
        title={`${greeting.text}, ${firstName}`}
        subtitle={getFormattedDate()}
        actions={
          <Chip
            label="Sistema activo"
            size="small"
            color="success"
            sx={{
              fontWeight: 600,
              fontSize: '0.6875rem',
              height: 22,
            }}
          />
        }
      />

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3.5 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Usuarios"
            value={stats?.users.total.toLocaleString()}
            icon={<People />}
            color="#5980a6"
            subtitle={stats?.users.subtitle}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Empresas"
            value={stats?.companies.total}
            icon={<Business />}
            color="#5980a6"
            subtitle={stats?.companies.subtitle}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Expedientes"
            value={stats?.proceedings.total}
            icon={<Folder />}
            color="#5980a6"
            subtitle={stats?.proceedings.subtitle}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Correspondencia"
            value={stats?.correspondence.total}
            icon={<Send />}
            color="#5980a6"
            subtitle={stats?.correspondence.subtitle}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Documentos"
            value={stats?.documents?.total ?? 0}
            icon={<InsertDriveFile />}
            color="#5980a6"
            subtitle={stats?.documents?.subtitle}
          />
        </Grid>
      </Grid>

      {/* Quick Access */}
      <QuickAccessWidget actions={quickActions} />
    </Box>
  );
};

export default DashboardPage;
